"use client";

/**
 * ScrollProgressBar — Linear.app-style fixed-top progress indicator.
 *
 * Why this, not full sticky nav: landing has an 8-second decision window
 * (see research/landing-v2/06-niche-live-tour.md §11). Full scene-jump
 * IntersectionObserver rail = academic overkill for cold traffic. A single
 * amber line shows shape-of-story without demanding interaction.
 *
 * Motion: Framer Motion useScroll() → scrollYProgress (0-1), smoothed via
 * useSpring, mapped to scaleX with origin-left.
 *
 * Reduced-motion: respects useReducedMotion() — falls back to a hidden
 * element so it doesn't animate on every scroll. The bar isn't load-bearing
 * for navigation; it's a cinematic cue.
 *
 * Accessibility: pointer-events-none so it never blocks clicks; no aria role
 * (decorative progress indicator, not a landmark).
 */

import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";

export function ScrollProgressBar() {
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  if (prefersReducedMotion) {
    return null;
  }

  // Wireframe §9.1: amber fill on top of a subtle bg-white/5 rail track.
  // Rail is a sibling container; motion.div child scales horizontally from
  // origin-left to reveal scroll progress.
  return (
    <div
      aria-hidden="true"
      className="fixed left-0 right-0 top-0 z-50 h-0.5 bg-white/5 pointer-events-none"
    >
      <motion.div
        className="h-full origin-left bg-gradient-to-r from-amber-400 to-amber-300"
        style={{ scaleX }}
      />
    </div>
  );
}
